import type { OHLCData, TimeFrame } from "./types";

/** Client-side OHLC aggregation (used for 1w / 1mn and as fallback). */
export const aggregateOHLCData = (
  data: OHLCData[],
  timeframe: TimeFrame,
): OHLCData[] => {
  if (timeframe === "1m" || data.length === 0) return data;

  const aggregated: OHLCData[] = [];
  let currentBucket: OHLCData | null = null;

  for (const candle of data) {
    const date = new Date(candle.timestamp);
    const bucketTime = new Date(date);

    if (timeframe === "5m") {
      bucketTime.setMinutes(Math.floor(date.getMinutes() / 5) * 5);
    } else if (timeframe === "15m") {
      bucketTime.setMinutes(Math.floor(date.getMinutes() / 15) * 15);
    } else if (timeframe === "30m") {
      bucketTime.setMinutes(Math.floor(date.getMinutes() / 30) * 30);
    } else if (timeframe === "1h") {
      bucketTime.setMinutes(0);
    } else if (timeframe === "2h") {
      bucketTime.setHours(Math.floor(date.getHours() / 2) * 2);
      bucketTime.setMinutes(0);
    } else if (timeframe === "4h") {
      bucketTime.setHours(Math.floor(date.getHours() / 4) * 4);
      bucketTime.setMinutes(0);
    } else if (timeframe === "12h") {
      bucketTime.setHours(Math.floor(date.getHours() / 12) * 12);
      bucketTime.setMinutes(0);
    } else if (timeframe === "1d") {
      bucketTime.setHours(0);
      bucketTime.setMinutes(0);
    } else if (timeframe === "1w") {
      const dayOfWeek = date.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      bucketTime.setDate(date.getDate() - daysToSubtract);
      bucketTime.setHours(0);
      bucketTime.setMinutes(0);
    } else if (timeframe === "1mn") {
      bucketTime.setDate(1);
      bucketTime.setHours(0);
      bucketTime.setMinutes(0);
    }

    bucketTime.setSeconds(0);
    bucketTime.setMilliseconds(0);

    const bucketTimestamp = bucketTime.getTime();

    if (!currentBucket || currentBucket.timestamp !== bucketTimestamp) {
      if (currentBucket) {
        aggregated.push(currentBucket);
      }
      currentBucket = {
        timestamp: bucketTimestamp,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: candle.volume,
      };
    } else {
      currentBucket.high = Math.max(currentBucket.high, candle.high);
      currentBucket.low = Math.min(currentBucket.low, candle.low);
      currentBucket.close = candle.close;
      currentBucket.volume += candle.volume;
    }
  }

  if (currentBucket) {
    aggregated.push(currentBucket);
  }

  return aggregated;
};

export function getTimeframeIntervalMs(timeframe: TimeFrame): number | null {
  switch (timeframe) {
    case "1m":
      return 60_000;
    case "5m":
      return 5 * 60_000;
    case "15m":
      return 15 * 60_000;
    case "30m":
      return 30 * 60_000;
    case "1h":
      return 60 * 60_000;
    case "2h":
      return 2 * 60 * 60_000;
    case "4h":
      return 4 * 60 * 60_000;
    case "12h":
      return 12 * 60 * 60_000;
    case "1d":
      return 24 * 60 * 60_000;
    case "1w":
    case "1mn":
      return null;
    default:
      return 60_000;
  }
}

export function needsJsAggregation(timeframe: TimeFrame): boolean {
  return timeframe === "1w" || timeframe === "1mn";
}
